// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Reputation Manager - Track player standing with alien species
 * 
 * Manages reputation scores for all alien species encountered.
 * Reputation ranges from -100 (ENEMY) to +100 (ALLIED).
 * 
 * Reputation Levels:
 * - ENEMY: -100 to -50 (Always hostile, no trade, attack on sight)
 * - UNFRIENDLY: -49 to -10 (Suspicious, poor prices, may attack)
 * - NEUTRAL: -9 to 9 (Standard interactions)
 * - FRIENDLY: 10 to 49 (Better prices, helpful dialogue)
 * - ALLIED: 50 to 100 (Best prices, special missions, aid in combat)
 */

const REPUTATION_STORAGE_KEY = 'alienReputation';

// Reputation level thresholds
const REPUTATION_LEVELS = {
  ENEMY: { min: -100, max: -50, label: 'ENEMY', color: '#ff4444' },
  UNFRIENDLY: { min: -49, max: -10, label: 'UNFRIENDLY', color: '#ff8800' },
  NEUTRAL: { min: -9, max: 9, label: 'NEUTRAL', color: '#ffaa00' },
  FRIENDLY: { min: 10, max: 49, label: 'FRIENDLY', color: '#00ffff' },
  ALLIED: { min: 50, max: 100, label: 'ALLIED', color: '#00ff88' }
};

/**
 * Load reputation data from localStorage
 * @returns {Object} Reputation data object
 */
const loadReputation = () => {
  try {
    const stored = localStorage.getItem(REPUTATION_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('[ReputationManager] Error loading reputation:', error);
    return {};
  }
};

/**
 * Save reputation data to localStorage
 * @param {Object} reputationData - Reputation data to save
 */
const saveReputation = (reputationData) => {
  try {
    localStorage.setItem(REPUTATION_STORAGE_KEY, JSON.stringify(reputationData));
  } catch (error) {
    console.error('[ReputationManager] Error saving reputation:', error);
  }
};

/**
 * Get reputation score for a species
 * @param {string} speciesId - Alien species ID
 * @returns {number} Reputation score (-100 to 100)
 */
export const getReputation = (speciesId) => {
  if (!speciesId) return 0;
  
  const reputation = loadReputation();
  return reputation[speciesId] || 0;
};

/**
 * Modify reputation for a species
 * @param {string} speciesId - Alien species ID
 * @param {number} amount - Amount to change (positive or negative)
 * @param {string} reason - Reason for change (for logging)
 * @returns {number} New reputation score
 */
export const modifyReputation = (speciesId, amount, reason = '') => {
  if (!speciesId || typeof amount !== 'number') {
    console.warn('[ReputationManager] Invalid parameters:', { speciesId, amount });
    return 0;
  }
  
  const reputation = loadReputation();
  const currentRep = reputation[speciesId] || 0;
  const newRep = Math.max(-100, Math.min(100, currentRep + amount));
  
  reputation[speciesId] = newRep;
  
  // Faction spillover: slightly affect other species from the same origin
  const SPILLOVER_RATIO = 0.2; // 20% of the change spills to faction-mates
  try {
    const { getAlienSpecies, getAliensByOrigin } = require('../../config/AlienSpeciesConfig');
    const species = getAlienSpecies(speciesId);
    if (species?.origin && Math.abs(amount) >= 5) {
      const factionSpecies = getAliensByOrigin(species.origin);
      factionSpecies.forEach(mate => {
        if (mate.id !== speciesId) {
          const mateRep = reputation[mate.id] || 0;
          const spillover = Math.round(amount * SPILLOVER_RATIO);
          if (spillover !== 0) {
            reputation[mate.id] = Math.max(-100, Math.min(100, mateRep + spillover));
          }
        }
      });
    }
  } catch { /* AlienSpeciesConfig not available */ }
  
  saveReputation(reputation);
  
  // Log reputation change
  
  // Dispatch event for UI updates using EventBus
  const EventBus = require('../system/EventBus').default;
  const { EVENT_TYPES } = require('../system/EventBus');
  EventBus.emit(EVENT_TYPES.REPUTATION_CHANGED, {
    speciesId,
    oldReputation: currentRep,
    newReputation: newRep,
    change: amount,
    reason
  });
  
  return newRep;
};

/**
 * Set reputation for a species (absolute value)
 * @param {string} speciesId - Alien species ID
 * @param {number} value - New reputation value (-100 to 100)
 * @returns {number} New reputation score
 */
export const setReputation = (speciesId, value) => {
  if (!speciesId || typeof value !== 'number') {
    console.warn('[ReputationManager] Invalid parameters:', { speciesId, value });
    return 0;
  }
  
  const reputation = loadReputation();
  const clampedValue = Math.max(-100, Math.min(100, value));
  
  reputation[speciesId] = clampedValue;
  saveReputation(reputation);
  
  
  return clampedValue;
};

/**
 * Get reputation level for a species
 * @param {string} speciesId - Alien species ID
 * @returns {Object} Reputation level object { label, color, min, max }
 */
export const getReputationLevel = (speciesId) => {
  const rep = getReputation(speciesId);
  
  for (const level of Object.values(REPUTATION_LEVELS)) {
    if (rep >= level.min && rep <= level.max) {
      return level;
    }
  }
  
  return REPUTATION_LEVELS.NEUTRAL;
};

/**
 * Get reputation level from score
 * @param {number} reputationScore - Reputation score
 * @returns {Object} Reputation level object
 */
export const getReputationLevelFromScore = (reputationScore) => {
  for (const level of Object.values(REPUTATION_LEVELS)) {
    if (reputationScore >= level.min && reputationScore <= level.max) {
      return level;
    }
  }
  
  return REPUTATION_LEVELS.NEUTRAL;
};

/**
 * Check if species is hostile based on reputation
 * @param {string} speciesId - Alien species ID
 * @returns {boolean} True if hostile
 */
export const isHostile = (speciesId) => {
  const rep = getReputation(speciesId);
  return rep <= REPUTATION_LEVELS.ENEMY.max;
};

/**
 * Check if species is friendly based on reputation
 * @param {string} speciesId - Alien species ID
 * @returns {boolean} True if friendly or allied
 */
export const isFriendly = (speciesId) => {
  const rep = getReputation(speciesId);
  return rep >= REPUTATION_LEVELS.FRIENDLY.min;
};

/**
 * Check if species is allied based on reputation
 * @param {string} speciesId - Alien species ID
 * @returns {boolean} True if allied
 */
export const isAllied = (speciesId) => {
  const rep = getReputation(speciesId);
  return rep >= REPUTATION_LEVELS.ALLIED.min;
};

/**
 * Get all species reputations
 * @returns {Object} All reputation data
 */
export const getAllReputations = () => {
  return loadReputation();
};

/**
 * Get reputation statistics
 * @returns {Object} Statistics about reputations
 */
export const getReputationStats = () => {
  const reputation = loadReputation();
  const species = Object.keys(reputation);
  
  const stats = {
    totalSpecies: species.length,
    enemies: 0,
    unfriendly: 0,
    neutral: 0,
    friendly: 0,
    allied: 0,
    averageReputation: 0
  };
  
  if (species.length === 0) return stats;
  
  let totalRep = 0;
  
  species.forEach(speciesId => {
    const rep = reputation[speciesId];
    totalRep += rep;
    
    const level = getReputationLevelFromScore(rep);
    switch (level.label) {
      case 'ENEMY':
        stats.enemies++;
        break;
      case 'UNFRIENDLY':
        stats.unfriendly++;
        break;
      case 'NEUTRAL':
        stats.neutral++;
        break;
      case 'FRIENDLY':
        stats.friendly++;
        break;
      case 'ALLIED':
        stats.allied++;
        break;
      default:
        break;
    }
  });
  
  stats.averageReputation = Math.round(totalRep / species.length);
  
  return stats;
};

/**
 * Reset reputation for a species
 * @param {string} speciesId - Alien species ID
 */
export const resetReputation = (speciesId) => {
  const reputation = loadReputation();
  delete reputation[speciesId];
  saveReputation(reputation);
  
};

/**
 * Reset all reputations
 */
export const resetAllReputations = () => {
  localStorage.removeItem(REPUTATION_STORAGE_KEY);
  
  const EventBus = require('../system/EventBus').default;
  const { EVENT_TYPES } = require('../system/EventBus');
  EventBus.emit(EVENT_TYPES.REPUTATION_RESET);
};

/**
 * Get reputation color for display
 * @param {string} speciesId - Alien species ID
 * @returns {string} Color hex code
 */
export const getReputationColor = (speciesId) => {
  const level = getReputationLevel(speciesId);
  return level.color;
};

/**
 * Get reputation percentage (0-100 for display)
 * @param {string} speciesId - Alien species ID
 * @returns {number} Percentage 0-100
 */
export const getReputationPercentage = (speciesId) => {
  const rep = getReputation(speciesId);
  return Math.round(((rep + 100) / 200) * 100);
};

/**
 * Format reputation for display
 * @param {string} speciesId - Alien species ID
 * @returns {Object} Formatted reputation data
 */
export const formatReputation = (speciesId) => {
  const rep = getReputation(speciesId);
  const level = getReputationLevel(speciesId);
  const percentage = getReputationPercentage(speciesId);
  
  return {
    score: rep,
    level: level.label,
    color: level.color,
    percentage: percentage,
    isHostile: isHostile(speciesId),
    isFriendly: isFriendly(speciesId),
    isAllied: isAllied(speciesId)
  };
};

// Export reputation levels for reference
export const LEVELS = REPUTATION_LEVELS;

// ============================================================================
// FACTION REPUTATION
// ============================================================================

/**
 * Get faction (origin) for a species by looking up AlienSpeciesConfig.
 * @param {string} speciesId
 * @returns {string|null} Faction/origin name
 */
export const getFaction = (speciesId) => {
  try {
    const { getAlienSpecies } = require('../../config/AlienSpeciesConfig');
    const species = getAlienSpecies(speciesId);
    return species?.origin || null;
  } catch { return null; }
};

/**
 * Get average reputation across all known species in a faction.
 * @param {string} faction - Origin/faction name (e.g. 'Star Trek', 'Pokémon')
 * @returns {number} Average reputation score (-100 to 100), 0 if no data
 */
export const getFactionReputation = (faction) => {
  if (!faction) return 0;
  const all = loadReputation();
  let total = 0, count = 0;
  try {
    const { getAliensByOrigin } = require('../../config/AlienSpeciesConfig');
    const factionSpecies = getAliensByOrigin(faction);
    Object.keys(factionSpecies).forEach(id => {
      if (all[id] !== undefined) { total += all[id]; count++; }
    });
  } catch { /* fallback: scan all reps */ }
  return count > 0 ? Math.round(total / count) : 0;
};

/**
 * Get faction reputation level.
 * @param {string} faction
 * @returns {Object} Reputation level object
 */
export const getFactionReputationLevel = (faction) => {
  return getReputationLevelFromScore(getFactionReputation(faction));
};

/**
 * Get trade price multiplier based on faction reputation at a location.
 * ENEMY: 1.25 (25% markup), UNFRIENDLY: 1.10, NEUTRAL: 1.0, FRIENDLY: 0.90, ALLIED: 0.80
 * @param {string} faction - Faction/origin name
 * @returns {number} Price multiplier
 */
export const getFactionPriceMultiplier = (faction) => {
  const rep = getFactionReputation(faction);
  if (rep >= 50) return 0.80;   // ALLIED: 20% discount
  if (rep >= 10) return 0.90;   // FRIENDLY: 10% discount
  if (rep <= -50) return 1.25;  // ENEMY: 25% markup
  if (rep <= -10) return 1.10;  // UNFRIENDLY: 10% markup
  return 1.0;                   // NEUTRAL
};

// Default export with all functions
const ReputationManager = {
  getReputation,
  modifyReputation,
  setReputation,
  getReputationLevel,
  getReputationLevelFromScore,
  isHostile,
  isFriendly,
  isAllied,
  getAllReputations,
  getReputationStats,
  resetReputation,
  resetAllReputations,
  getReputationColor,
  getReputationPercentage,
  formatReputation,
  getFaction,
  getFactionReputation,
  getFactionReputationLevel,
  getFactionPriceMultiplier,
  LEVELS: REPUTATION_LEVELS
};

export default ReputationManager;
